import { Product, Variant } from "../../types/types";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { getVariant, isValidColour, isValidSize } from "./utils";

type Props = {
  product: Product;
  children: ReactNode;
};

type VariantContextType = {
  variant: Variant | null;
  isDefaultVariant: boolean;
  setSelectedColour: (colour: string) => void;
  setSelectedSize: (size: string) => void;
};

const VariantContext = createContext<VariantContextType | undefined>(undefined);

export function VariantProvider({
  children,
  product,
}: Props): React.JSX.Element {
  const [userSelectedColour, setUserSelectedColour] = useState<string | null>(
    null,
  );
  const [userSelectedSize, setUserSelectedSize] = useState<string | null>(null);

  const validateAndSetSelectedColour = (colour: string) => {
    if (isValidColour(colour, product)) {
      setUserSelectedColour(colour);
    }
  };

  const validateAndSetSelectedSize = (size: string) => {
    if (isValidSize(size, product)) {
      setUserSelectedSize(size);
    }
  };

  const variant = useMemo(
    () =>
      getVariant({
        product,
        userSelectedColour,
        userSelectedSize,
      }),
    [product, userSelectedColour, userSelectedSize],
  );

  return (
    <VariantContext.Provider
      value={{
        variant,
        isDefaultVariant: variant?.id === product.initialVariantId,
        setSelectedColour: validateAndSetSelectedColour,
        setSelectedSize: validateAndSetSelectedSize,
      }}
    >
      {children}
    </VariantContext.Provider>
  );
}

export function useVariant(): VariantContextType {
  const context = useContext(VariantContext);

  if (!context) {
    throw new Error("useVariant must be used within a VariantProvider");
  }

  return context;
}
